/**
 * Inbound-MIDI poller — the TRANSITIONAL half of the inbound-MIDI path.
 *
 * ⚠️ 2026-10-16: OPBridge HAS shipped the push arm (S242), in the shape of its
 * `/midi/recent` ring (status/data1/data2), not the proposed `number`/`value`;
 * our parser read only the proposed shape and dropped every pushed frame until
 * that date. Both now run, and each message is delivered once (`claimMidiIn`,
 * keyed on OPBridge's own timestamp). This poll does NOT stand down when the
 * push works: measured the same day on the Mac, the push carried the Pacer but
 * not the Midi Fighter Twister, which only this poll delivered. Delete it only
 * once the push is shown to carry every port. The history below is kept as
 * written.
 *
 * ⚠️ THIS MODULE IS MEANT TO BE DELETED. It exists because OPBridge's agreed
 * `midi_in` push arm on `/ws/consumer` is **not built and not scheduled**
 * (their words, 2026-08-27: "Not yet scheduled, and we are not going to
 * pretend otherwise"), while the PWA has had NO working inbound-MIDI path
 * since `packages/bridge/` was deleted 2026-08-20 — a regression OPBridge has
 * accepted as such. StatusLed (DEC-041) and MIDI-triggers-a-button
 * (DEC-112/154) both depend on it, and the operator gigs weekly.
 *
 * **Deleting it is a one-line change.** It feeds `emitMidiIn()`, the exact
 * sink `consumerWs.ts`'s `midi_in` arm feeds. No consumer knows which
 * transport delivered an event, so when OPBridge ships the push arm, removing
 * `startMidiInPoll()` from App.tsx and deleting this file is the whole job.
 *
 * ─── Why polling this route is delicate ────────────────────────────────────
 *
 * `GET /midi/recent` is a **DESTRUCTIVE DRAIN**: OPBridge's handler calls
 * `drain_recent()`, which POPS the ring (opbridge-core `handlers/midi.rs`).
 *
 * [CORRECTED 2026-11-03: since OPBridge S350 (1.15.250, `recent_fanout.rs`)
 * each KEY reads from its own cursor, so readers with different keys no
 * longer take from each other. Within this app (one key) a read still takes
 * what it returns, so the single-owner rule below still holds for us; the
 * cross-app half of point 2 is what changed.]
 *
 * Three consequences, all load-bearing:
 *
 *   1. **Only ONE thing may poll it.** Whoever reads first takes the
 *      messages; every other reader sees an empty ring. This module is
 *      therefore the single owner of that route in the running app.
 *   2. **It must not run when nothing is listening.** With no subscribers
 *      the poll would silently eat messages another consumer (OPBridge's own
 *      tooling, a future arm) might want. So the poll idles unless
 *      `onMidiIn()` has at least one listener.
 *   3. **Nothing else may poll it either.** MIDI Learn used to, and the two
 *      raced: the background poll stole the operator's stomp and Learn timed
 *      out having "seen nothing" while hardware, daemon and ring were all
 *      fine. Fixed by moving Learn onto the non-destructive traffic monitor
 *      (`midiTrigger/learn.ts`), not by coordinating locks. This poll is now
 *      the single owner of this route in the running app; keep it that way.
 *
 * ⚠️ **Why this module does NOT use `/traffic/midi/recent`**, even though that
 * route is non-destructive, sees every port and would be simpler: it is an
 * OBSERVABILITY surface. OPBridge may sample, rate-limit or perf-gate it (they
 * already keep 30 Hz `mixer_update` out of the consumer ring for exactly that
 * reason), and an operator denylist marks entries `filtered`. StatusLed must
 * survive a gig, so it rides the delivery plane; a monitor that degrades under
 * load would take the lamps dark precisely when Performance Mode is engaged —
 * i.e. on stage, after working perfectly all week. Learn is desk-time only and
 * may safely depend on the monitor. Prior art for the split: k8s
 * `metrics-server` declares itself invalid for consumers it was not tuned for;
 * AWS "static stability" says never make the serving path depend on a plane
 * that is allowed to degrade.
 *
 * ─── Cadence, chosen from measured hardware ────────────────────────────────
 *
 * 100 ms, faster than clockPoll's 250/500 ms, and deliberately fixed rather
 * than adaptive:
 *
 *   - This is a CONTROL INPUT. Its latency is felt directly on a footswitch,
 *     unlike the clock, which is interpolated between anchors. An adaptive
 *     "slow when idle" cadence would put the worst latency on exactly the
 *     first press after a quiet passage — the one that matters most on stage.
 *   - Overflow is not a risk at this rate. Measured on a real Midi Fighter
 *     Twister 2026-08-27: one encoder turn emits ~142 CC messages. OPBridge's
 *     ring holds `RECENT_BUFFER_CAPACITY = 256` and is **drop-OLDEST** on
 *     overflow (`lib.rs` pops then re-pushes), so a press is never lost to
 *     encoder spam — only stale spam is evicted. Overflowing between ticks
 *     would take ~1.8 full encoder sweeps inside 100 ms.
 *   - Bursts are normal, not exceptional. A drain routinely returns tens of
 *     messages at once, so consumers must tolerate batches; that ~142-per-turn
 *     figure is the number to design against.
 */

import { bridges, getConnection } from './store.ts';
import type { BridgeConfig } from './storage.ts';
import type { MidiRecentEntry } from './httpClient.ts';
import { claimMidiIn, emitMidiIn, midiInListenerCount, midiKindFor, type MidiInEvent } from './consumerWs.ts';
import { watchJob } from '../diag/watchdog.ts';

/** See the cadence note in the module doc comment. */
const POLL_INTERVAL_MS = 100;
/** Backoff while there is no connected primary / nobody listening. */
const IDLE_INTERVAL_MS = 500;

let timer: ReturnType<typeof setTimeout> | null = null;
let stopped = true;
let inFlight = false;

// The status-byte mapping lives in `consumerWs.ts::midiKindFor`, shared with
// the push arm so the two transports cannot disagree. ⚠️ `status` arrives with
// the channel nibble ALREADY MASKED OFF (OPBridge's `parse_channel_voice`);
// byte-verified 2026-08-27.

/** Translate one ring entry into the `/ws/consumer` contract's event shape,
 *  so a consumer cannot tell which transport delivered it. Returns null for
 *  anything that is not a channel-voice message we model. */
function toEvent(e: MidiRecentEntry, ownerNodeId: string): MidiInEvent | null {
  if (typeof e.status !== 'number' || typeof e.data1 !== 'number') return null;
  // Absent channel means a non-channel-voice message; OPBridge omits the
  // field rather than zeroing it, so it must not be read as channel 0.
  if (typeof e.channel !== 'number') return null;
  const kind = midiKindFor(e.status, e.data2);
  if (kind === null) return null;
  return {
    // The drain is a LOCAL read from the primary, so the bridge we polled
    // both owns the port and delivered the frame. `viaNodeId` is empty
    // rather than duplicated: on the real push path it means "relayed by",
    // and claiming a relay that never happened would be a fabricated fact.
    portOwnerNodeId: ownerNodeId,
    viaNodeId: '',
    port: e.port,
    channel: e.channel,
    kind,
    number: e.data1,
    value: e.data2 ?? 0,
    bytes: Array.isArray(e.bytes) ? e.bytes : [],
  };
}

function pickPrimaryConnected(): BridgeConfig | null {
  // DEC-063: engine + clock + inbound state all come from the primary.
  const all = bridges();
  if (all.length === 0) return null;
  const primary = all.find((cfg) => cfg.is_primary) ?? all[0]!;
  const conn = getConnection(primary.id);
  return conn && conn.status() === 'connected' ? primary : null;
}

function schedule(delayMs: number): void {
  if (stopped) return;
  timer = setTimeout(() => { timer = null; void tick(); }, delayMs);
}

async function tick(): Promise<void> {
  if (stopped || inFlight) return;
  inFlight = true;
  try {
    await tickInner();
  } finally {
    inFlight = false;
  }
}

async function tickInner(): Promise<void> {
  // Never drain while nothing is listening - this route POPS the ring, so a
  // poll with no consumer destroys messages someone else may need.
  if (midiInListenerCount() === 0) {
    schedule(IDLE_INTERVAL_MS);
    return;
  }
  // NOT stood down while OPBridge's push is live (2026-10-16, measured on the
  // operator's Mac): the push carried the Pacer but, with the socket open, not
  // one of ~107 Midi Fighter Twister messages that this poll delivered. A
  // stand-down keyed on "the push works" would have starved the Twister. Each
  // message is delivered once whichever transport brings it (`claimMidiIn`).
  const cfg = pickPrimaryConnected();
  if (!cfg) {
    schedule(IDLE_INTERVAL_MS);
    return;
  }
  watchJob('midi-in-poll');
  const conn = getConnection(cfg.id);
  if (!conn) {
    schedule(IDLE_INTERVAL_MS);
    return;
  }
  try {
    const entries = await conn.httpClient().midiRecent();
    const ownerNodeId = cfg.nodeId ?? '';
    for (const e of entries) {
      const evt = toEvent(e, ownerNodeId);
      // Already delivered by the push (the ring keeps what the push sent while this poll stood down).
      if (evt && typeof e.timestamp_us === 'number' && !claimMidiIn(e.port, e.timestamp_us, e.bytes ?? [])) continue;
      if (evt) emitMidiIn(evt);
    }
  } catch {
    // 401 (bearer lacks /midi/recent), 503, network blip - all expected and
    // transient. Same posture as clockPoll: a miss just means no inbound
    // this tick. Deliberately silent; a console error per 100ms tick during
    // a bridge bounce would bury everything else in the log.
  }
  schedule(POLL_INTERVAL_MS);
}

/** Start polling. Idempotent. */
export function startMidiInPoll(): void {
  if (!stopped) return;
  stopped = false;
  schedule(IDLE_INTERVAL_MS);
}

/** Stop and tear down. Used by App onCleanup + tests. */
export function stopMidiInPoll(): void {
  stopped = true;
  if (timer !== null) { clearTimeout(timer); timer = null; }
}

// NOTE: `suspendMidiInPoll()` used to live here, so MIDI Learn could hold this
// poll off while it drained the same destructive route. Deleted 2026-08-27 -
// Learn now reads OPBridge's NON-destructive traffic monitor instead
// (`midiTrigger/learn.ts`), so there is no longer a second drainer to
// coordinate with. Removing the coordination beats getting it right: a
// suspend/resume pair is one stranded `finally` away from silently killing
// inbound MIDI for the rest of a session.
